import {
  type BreakdownSelection,
  getBreakdownId,
  isGroupableBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'

// Hand-kept twin of the backend's `SANKEY_MIN_COLUMNS` / `SANKEY_MAX_COLUMNS` /
// `sankey_columns`, which refuse an Ask Yogi request outside these bounds before
// it runs. Nothing fails when the two drift: the agent would be told a flow is
// drawable that this chart refuses, or the reverse. Change both together.
export const SANKEY_MIN_COLUMNS = 2
export const SANKEY_MAX_COLUMNS = 4

/**
 * 'group' is the rolled-up parent column of a grouped breakdown; 'value' is a
 * column of the breakdown's real values (the member column of a grouped one).
 */
export type SankeyColumnLevel = 'group' | 'value'

export interface SankeyColumn {
  /** Position of the breakdown in the panel, which is also its index in a wire
   *  row's `category`. */
  breakdownIndex: number
  level: SankeyColumnLevel
}

export const isGroupedSankeyBreakdown = (
  breakdown: BreakdownSelection | undefined,
): boolean => isGroupableBreakdown(breakdown) && breakdown.grouped

/**
 * The columns a panel's breakdowns lay out, in flow order. A grouped breakdown
 * takes two (group, then member); every other breakdown takes one.
 */
export const getSankeyColumns = (
  breakdowns: readonly BreakdownSelection[],
): SankeyColumn[] =>
  breakdowns.flatMap((breakdown, breakdownIndex): SankeyColumn[] => {
    if (!breakdown) return []
    return isGroupedSankeyBreakdown(breakdown)
      ? [
          { breakdownIndex, level: 'group' },
          { breakdownIndex, level: 'value' },
        ]
      : [{ breakdownIndex, level: 'value' }]
  })

/** Shared by validation and the constraint tooltip so both agree on what counts. */
export const countSankeyColumns = (
  breakdowns: readonly BreakdownSelection[],
): number => getSankeyColumns(breakdowns).length

/** True when two breakdowns select the same field (a flow of a field into itself). */
export const hasDuplicateSankeyField = (
  breakdowns: readonly BreakdownSelection[],
): boolean => {
  const ids = breakdowns.flatMap((breakdown) =>
    breakdown ? [getBreakdownId(breakdown)] : [],
  )
  return new Set(ids).size !== ids.length
}
